"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DefaultDashboardAttributeFilter = void 0;
var tslib_1 = require("tslib");
// (C) 2021-2023 GoodData Corporation
var react_1 = tslib_1.__importStar(require("react"));
var react_intl_1 = require("react-intl");
var sdk_ui_filters_1 = require("@gooddata/sdk-ui-filters");
var dashboardFilterConverter_1 = require("../../../_staging/dashboard/dashboardFilterConverter");
var useParentFilters_1 = require("./useParentFilters");
var sdk_model_1 = require("@gooddata/sdk-model");
var AttributeFilterConfiguration_1 = require("./dashboardDropdownBody/configuration/AttributeFilterConfiguration");
var CustomDropdownActions_1 = require("./CustomDropdownActions");
var model_1 = require("../../../model");
var AttributeFilterParentFilteringContext_1 = require("./AttributeFilterParentFilteringContext");
var sdk_ui_kit_1 = require("@gooddata/sdk-ui-kit");
var useAttributes_1 = require("../../../_staging/sharedHooks/useAttributes");
/**
 * Default implementation of the attribute filter to use on the dashboard's filter bar.
 *
 * This will use the SDK's AttributeFilter with the button styled same as we have it today on KD.
 *
 * @alpha
 */
exports.DefaultDashboardAttributeFilter = function (props) {
    var filter = props.filter, onFilterChanged = props.onFilterChanged, isDraggable = props.isDraggable, autoOpen = props.autoOpen, onClose = props.onClose;
    var _a = useParentFilters_1.useParentFilters(filter), parentFilters = _a.parentFilters, parentFilterOverAttribute = _a.parentFilterOverAttribute;
    var locale = model_1.useDashboardSelector(model_1.selectLocale);
    var attributeFilter = react_1.useMemo(function () { return dashboardFilterConverter_1.dashboardAttributeFilterToAttributeFilter(filter); }, [filter]);
    var _b = tslib_1.__read(react_1.useState(false), 2), isConfigurationOpen = _b[0], setIsConfigurationOpen = _b[1];
    var filterRef = react_1.useMemo(function () {
        return sdk_model_1.filterObjRef(attributeFilter);
    }, [attributeFilter]);
    var dispatch = model_1.useDashboardDispatch();
    var handleRemoveAttributeFilter = react_1.useCallback(function () { return dispatch(model_1.removeAttributeFilter(filter.attributeFilter.localIdentifier)); }, [filter, dispatch]);
    var intl = react_intl_1.useIntl();
    var cancelText = intl.formatMessage({ id: "gs.list.cancel" });
    var saveText = intl.formatMessage({ id: "attributesDropdown.save" });
    var applyText = intl.formatMessage({ id: "gs.list.apply" });
    var displayValuesAsText = intl.formatMessage({ id: "attributesDropdown.displayValuesAs" });
    var filterByText = intl.formatMessage({ id: "attributesDropdown.filterBy" });
    var onCloseFilter = react_1.useCallback(function () {
        if (onClose) {
            onClose();
        }
    }, [onClose]);
    var attributeFilterRef = react_1.useMemo(function () {
        return [filterRef];
    }, [filterRef]);
    var attributes = useAttributes_1.useAttributes(attributeFilterRef).attributes;
    var CustomDropdownButton = react_1.useMemo(function () {
        return function DropdownButton(props) {
            sdk_ui_filters_1.useAutoOpenAttributeFilterDropdownButton(props, !!autoOpen);
            sdk_ui_filters_1.useOnCloseAttributeFilterDropdownButton(props, onCloseFilter);
            return react_1.default.createElement(sdk_ui_filters_1.AttributeFilterDropdownButton, tslib_1.__assign({}, props, { isDraggable: isDraggable }));
        };
    }, [isDraggable, autoOpen, onCloseFilter]);
    var CustomDropdownActions = react_1.useMemo(function () {
        return function DropdownActions(props) {
            var _a = AttributeFilterParentFilteringContext_1.useAttributeFilterParentFiltering(), onConfigurationSave = _a.onConfigurationSave, configurationChanged = _a.configurationChanged, displayFormChanged = _a.displayFormChanged, onConfigurationClose = _a.onConfigurationClose;
            return (react_1.default.createElement(react_1.default.Fragment, null, isConfigurationOpen ? (react_1.default.createElement(CustomDropdownActions_1.CustomConfigureAttributeFilterDropdownActions, { isSaveDisabled: !(configurationChanged || displayFormChanged), onSaveButtonClick: function () {
                    onConfigurationSave();
                    setIsConfigurationOpen(false);
                }, onCancelButtonClick: function () {
                    setIsConfigurationOpen(false);
                }, cancelText: cancelText, saveText: saveText })) : (react_1.default.createElement(CustomDropdownActions_1.CustomAttributeFilterDropdownActions, tslib_1.__assign({}, props, { filterDisplayFormRef: filter.attributeFilter.displayForm, applyText: applyText, cancelText: cancelText, onConfigurationButtonClick: function () {
                    setIsConfigurationOpen(true);
                    onConfigurationClose();
                }, onDeleteButtonClick: function () {
                    handleRemoveAttributeFilter();
                }, attributes: attributes })))));
        };
    }, [
        isConfigurationOpen,
        cancelText,
        saveText,
        filter.attributeFilter.displayForm,
        applyText,
        attributes,
        handleRemoveAttributeFilter,
    ]);
    var CustomElementsSelect = react_1.useMemo(function () {
        return function ElementsSelect(props) {
            var displayFormChangeStatus = AttributeFilterParentFilteringContext_1.useAttributeFilterParentFiltering().displayFormChangeStatus;
            var closeHandler = react_1.useCallback(function () {
                setIsConfigurationOpen(false);
            }, []);
            if (displayFormChangeStatus === "running") {
                return react_1.default.createElement(sdk_ui_kit_1.LoadingMask, { height: sdk_ui_kit_1.LOADING_HEIGHT });
            }
            return (react_1.default.createElement(react_1.default.Fragment, null, isConfigurationOpen ? (react_1.default.createElement(AttributeFilterConfiguration_1.AttributeFilterConfiguration, { closeHandler: closeHandler, filterRef: filterRef, filterByText: filterByText, displayValuesAsText: displayValuesAsText })) : (react_1.default.createElement(sdk_ui_filters_1.AttributeFilterElementsSelect, tslib_1.__assign({}, props)))));
        };
    }, [isConfigurationOpen, filterRef, filterByText, displayValuesAsText]);
    if (!attributes) {
        return null;
    }
    return (react_1.default.createElement(AttributeFilterParentFilteringContext_1.AttributeFilterParentFilteringProvider, { filter: filter, attributes: attributes },
        react_1.default.createElement(sdk_ui_filters_1.AttributeFilterButton, { resetOnParentFilterChange: false, filter: attributeFilter, onApply: function (newFilter) {
                onFilterChanged(dashboardFilterConverter_1.attributeFilterToDashboardAttributeFilter(newFilter, filter.attributeFilter.localIdentifier));
            }, parentFilters: parentFilters, parentFilterOverAttribute: parentFilterOverAttribute, locale: locale, DropdownButtonComponent: CustomDropdownButton, DropdownActionsComponent: CustomDropdownActions, ElementsSelectComponent: CustomElementsSelect, fullscreenOnMobile: true })));
};
//# sourceMappingURL=DefaultDashboardAttributeFilter.js.map